import { useEffect, useRef, useState, useCallback } from "react";
import { createChart, CandlestickSeries, HistogramSeries, LineStyle, createSeriesMarkers } from "lightweight-charts";
import { RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

/** Native live chart (TradingView Lightweight Charts, Apache-2.0).
 *  DHAN realtime candles for Indian symbols — no widget restrictions,
 *  with the AI trade plan (Entry/SL/T1/T2) drawn directly on the chart. */
const POLL_MS = 5000;
const IST_OFFSET = 19800; // display timestamps in IST

const INTERVALS = ["1m", "5m", "15m"];

// FX trades in 4-5 decimals; indices in 2. Without this every EURUSD label collapses to "1.15".
const precisionFor = (p) => (p >= 100 ? 2 : p >= 1 ? 4 : 5);

function toBar(c) {
  const time = Math.floor(c.t / 1000) + IST_OFFSET;
  return { time, open: c.o, high: c.h, low: c.l, close: c.c };
}
function toVol(c) {
  return {
    time: Math.floor(c.t / 1000) + IST_OFFSET,
    value: c.v || 0,
    color: c.c >= c.o ? "rgba(34,197,94,0.35)" : "rgba(239,68,68,0.35)",
  };
}

export default function LightweightChart({ instrument, signal, strategy, candleState }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const volSeriesRef = useRef(null);
  const lastTRef = useRef(0);
  const genRef = useRef(0);
  const sourceRef = useRef("");
  const priceLinesRef = useRef([]);
  const markersRef = useRef(null);
  const [interval_, setIntervalTf] = useState("5m");
  const [status, setStatus] = useState("loading"); // loading | live | error
  const [errMsg, setErrMsg] = useState("");
  const [lastClose, setLastClose] = useState(null);

  const symbol = instrument?.symbol;

  // Chart lifecycle
  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      autoSize: true,
      localization: { locale: "en-IN" },
      layout: { background: { color: "#0d0d0d" }, textColor: "#71717a", fontSize: 11 },
      grid: { vertLines: { color: "#1a1a1a" }, horzLines: { color: "#1a1a1a" } },
      timeScale: { timeVisible: true, secondsVisible: false, borderColor: "#27272a", rightOffset: 4 },
      rightPriceScale: { borderColor: "#27272a" },
      crosshair: { horzLine: { labelBackgroundColor: "#007AFF" }, vertLine: { labelBackgroundColor: "#007AFF" } },
    });
    const candles = chart.addSeries(CandlestickSeries, {
      upColor: "#22C55E", downColor: "#EF4444", borderVisible: false,
      wickUpColor: "#22C55E", wickDownColor: "#EF4444",
    });
    const vol = chart.addSeries(HistogramSeries, {
      priceScaleId: "vol", priceFormat: { type: "volume" }, lastValueVisible: false, priceLineVisible: false,
    });
    chart.priceScale("vol").applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
    chartRef.current = chart;
    candleSeriesRef.current = candles;
    volSeriesRef.current = vol;
    markersRef.current = createSeriesMarkers(candles, []);
    return () => { chart.remove(); chartRef.current = null; markersRef.current = null; };
  }, []);

  const load = useCallback(async (full) => {
    if (!symbol || !candleSeriesRef.current) return;
    const gen = genRef.current; // discard responses that arrive after a symbol/interval switch
    try {
      const { data } = await api.get("/chart/candles", {
        params: {
          symbol, interval: interval_, limit: full ? 400 : 6,
          tv_symbol: instrument?.tv_symbol || "", category: instrument?.category || "",
        },
      });
      if (gen !== genRef.current || !candleSeriesRef.current) return; // stale — a newer symbol owns the chart
      // Defense-in-depth: never render a payload for a different symbol than requested.
      if ((data?.symbol || "").toUpperCase() !== (symbol || "").toUpperCase()) return;
      // Data source flipped (e.g. dhan→fallback) mid-poll: values/clocks differ between
      // providers — tail-merging them paints phantom spike candles. Full reload instead.
      const src = data?.source || "";
      if (!full && sourceRef.current && src !== sourceRef.current) {
        sourceRef.current = src;
        load(true);
        return;
      }
      sourceRef.current = src;
      const cs = (data?.candles || []).filter(
        (c) => [c.o, c.h, c.l, c.c].every((v) => typeof v === "number" && isFinite(v)) && c.h >= c.l
      );
      if (!cs.length) {
        if (full) {
          setStatus("error");
          setErrMsg("No candle data for this symbol right now");
        }
        return;
      }
      if (full) {
        candleSeriesRef.current.setData(cs.map(toBar));
        volSeriesRef.current.setData(cs.map(toVol));
        lastTRef.current = cs[cs.length - 1].t;
        const prec = precisionFor(cs[cs.length - 1].c);
        try {
          candleSeriesRef.current.applyOptions({
            priceFormat: { type: "price", precision: prec, minMove: Number((10 ** -prec).toFixed(prec)) },
          });
        } catch (e) { console.warn("price format failed:", e?.message || e); }
        // CRITICAL: a manual axis drag/zoom permanently disables autoscale on the
        // right price scale — after a symbol switch the old range (e.g. ₹83-88)
        // sticks and the new symbol's candles (e.g. 24,263) render OFF-SCREEN,
        // looking like "chart not rendering". Force autoscale + refit every load.
        try {
          chartRef.current?.priceScale("right").applyOptions({ autoScale: true });
          chartRef.current?.timeScale().resetTimeScale();
          chartRef.current?.timeScale().scrollToRealTime();
        } catch (e) { console.warn("chart rescale failed:", e?.message || e); }
      } else {
        for (const c of cs) {
          if (c.t >= lastTRef.current) {
            candleSeriesRef.current.update(toBar(c));
            volSeriesRef.current.update(toVol(c));
            lastTRef.current = c.t;
          }
        }
      }
      setLastClose(cs[cs.length - 1].c);
      setStatus("live");
    } catch (e) {
      if (gen !== genRef.current) return;
      if (full) {
        setStatus("error");
        setErrMsg(e?.response?.data?.detail || "No live data right now");
      }
    }
  }, [symbol, interval_, instrument?.tv_symbol, instrument?.category]);

  // Initial load + live polling on symbol/interval change
  useEffect(() => {
    if (!symbol) return;
    genRef.current += 1;            // invalidate any in-flight response from the previous symbol
    setStatus("loading");
    setLastClose(null);
    lastTRef.current = 0;
    try { candleSeriesRef.current?.setData([]); volSeriesRef.current?.setData([]); } catch (e) { console.warn("chart clear failed:", e?.message || e); }
    load(true);
    const id = setInterval(() => load(false), POLL_MS);
    return () => clearInterval(id);
  }, [symbol, interval_, load]);

  // Manual refresh — full clean reload without touching the rest of the page.
  const refresh = useCallback(() => {
    genRef.current += 1;
    setStatus("loading");
    lastTRef.current = 0;
    try { candleSeriesRef.current?.setData([]); volSeriesRef.current?.setData([]); } catch (e) { console.warn("chart clear failed:", e?.message || e); }
    load(true);
  }, [load]);

  // AI trade plan overlay lines
  useEffect(() => {
    const s = candleSeriesRef.current;
    if (!s) return;
    priceLinesRef.current.forEach((pl) => { try { s.removePriceLine(pl); } catch {} });
    priceLinesRef.current = [];
    try { markersRef.current?.setMarkers([]); } catch (e) { console.warn("marker clear failed:", e?.message || e); }
    if (!signal || signal.status === "no_trade" || (signal.symbol || "").toUpperCase() !== (symbol || "").toUpperCase()) return;
    const mk = (price, color, title) => {
      if (typeof price !== "number") return;
      priceLinesRef.current.push(s.createPriceLine({
        price, color, lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title,
      }));
    };
    mk(signal.entry_price, "#FFFFFF", "ENTRY");
    const r2 = (x) => Math.round(x * 100) / 100;
    const canAdj = typeof signal.entry_price === "number" && typeof signal.target_1 === "number" && typeof signal.stop_loss === "number";
    if (strategy === "s2" && canAdj) {
      const live = candleState?.s2_triggered;
      priceLinesRef.current.push(s.createPriceLine({
        price: signal.stop_loss, color: "#A855F7", lineWidth: live ? 2 : 1,
        lineStyle: live ? LineStyle.Solid : LineStyle.Dashed,
        axisLabelVisible: true, title: live ? "T·S2 LIVE" : "T·S2",
      }));
      if (live && typeof candleState.s2_stop === "number") {
        priceLinesRef.current.push(s.createPriceLine({
          price: candleState.s2_stop, color: "#EF4444", lineWidth: 2, lineStyle: LineStyle.Dashed,
          axisLabelVisible: true, title: "SL·S2",
        }));
      }
    } else if (strategy === "s1" && canAdj) {
      mk(signal.stop_loss, "#EF4444", "SL");
      // S1 books at 50% of T1 — keep the AI's original T1/T2 visible (fainter) for full-move context.
      priceLinesRef.current.push(s.createPriceLine({
        price: r2(signal.entry_price + 0.5 * (signal.target_1 - signal.entry_price)), color: "#22C55E",
        lineWidth: 2, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: "T·S1 (50%)",
      }));
      const faint = (price, title) => {
        if (typeof price !== "number") return;
        priceLinesRef.current.push(s.createPriceLine({
          price, color: "rgba(34,197,94,0.45)", lineWidth: 1, lineStyle: LineStyle.Dotted, axisLabelVisible: true, title,
        }));
      };
      faint(signal.target_1, "T1");
      faint(signal.target_2, "T2");
    } else {
      mk(signal.stop_loss, "#EF4444", "SL");
      mk(signal.target_1, "#22C55E", "T1");
      mk(signal.target_2, "#16A34A", "T2");
    }
    // Sweep zone — reversals statistically cluster just BEYOND the SL (stop-hunt).
    if (strategy !== "s2" && typeof signal.entry_price === "number" && typeof signal.stop_loss === "number" && signal.entry_price !== signal.stop_loss) {
      const risk = Math.abs(signal.entry_price - signal.stop_loss);
      const long = signal.stop_loss < signal.entry_price;
      const sweep = long ? signal.stop_loss - 0.35 * risk : signal.stop_loss + 0.35 * risk;
      priceLinesRef.current.push(s.createPriceLine({
        price: sweep, color: "#F59E0B", lineWidth: 1, lineStyle: LineStyle.Dotted,
        axisLabelVisible: true, title: "SWEEP ZONE",
      }));
    }
    // Mark the exact candle where this signal was generated.
    if (signal.generated_at) {
      const genMs = Date.parse(signal.generated_at);
      if (!Number.isNaN(genMs)) {
        const tfSec = { "1m": 60, "5m": 300, "15m": 900 }[interval_] || 300;
        const t = Math.floor(genMs / 1000 / tfSec) * tfSec + IST_OFFSET;
        const long = typeof signal.stop_loss === "number" && typeof signal.entry_price === "number"
          ? signal.stop_loss < signal.entry_price : true;
        try {
          markersRef.current?.setMarkers([{
            time: t,
            position: long ? "belowBar" : "aboveBar",
            color: "#007AFF",
            shape: long ? "arrowUp" : "arrowDown",
            text: "SIGNAL",
            size: 1.5,
          }]);
        } catch (e) { console.warn("signal marker failed:", e?.message || e); }
      }
    }
  }, [signal, symbol, status, interval_, strategy, candleState]);

  return (
    <div className="relative" data-testid="lightweight-chart">
      {/* Interval switcher + live dot */}
      <div className="absolute top-2 left-2 z-10 flex items-center gap-1">
        {INTERVALS.map((tf) => (
          <button key={tf} onClick={() => setIntervalTf(tf)}
                  className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest border ${interval_ === tf ? "border-[#007AFF] text-[#3B9BFF] bg-[#007AFF]/10" : "border-zinc-800 text-zinc-500 hover:text-white"}`}
                  data-testid={`chart-tf-${tf}`}>
            {tf}
          </button>
        ))}
        <button onClick={refresh} title="Reload chart data"
                className="px-1.5 py-0.5 border border-zinc-800 text-zinc-500 hover:text-white hover:border-zinc-600"
                data-testid="chart-refresh-btn">
          <RefreshCw className={`h-3 w-3 ${status === "loading" ? "animate-spin" : ""}`} />
        </button>
        {status === "live" && (
          <span className="ml-2 inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-[#22C55E]" data-testid="chart-live-dot">
            <span className="h-1.5 w-1.5 rounded-full bg-[#22C55E] animate-pulse" /> Live
            {lastClose != null && (
              <span className="ml-1 text-zinc-300 normal-case" data-testid="chart-last-price">{Number(lastClose).toLocaleString("en-IN", { maximumFractionDigits: precisionFor(Number(lastClose)) })}</span>
            )}
          </span>
        )}
      </div>

      <div ref={containerRef} className="w-full h-[420px] md:h-[480px]" />

      {strategy === "s2" && candleState?.s2_triggered && (
        <div className="absolute top-12 left-3 z-10 flex items-center gap-2 border border-[#A855F7] bg-[#A855F7]/15 backdrop-blur px-3 py-1.5"
             data-testid="contra-trade-on-bubble">
          <span className="h-2 w-2 rounded-full bg-[#A855F7] animate-pulse" />
          <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-[#D8B4FE]">Contra Trade ON</span>
        </div>
      )}

      {status === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#0d0d0d]/70 text-[10px] font-mono uppercase tracking-widest text-zinc-500" data-testid="chart-loading">
          ◢ Loading live candles…
        </div>
      )}
      {status === "error" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#0d0d0d]/85 px-6 text-center text-xs text-zinc-400" data-testid="chart-error">
          <span>{errMsg} — the chart resumes automatically during market hours.</span>
          <button onClick={refresh}
                  className="inline-flex items-center gap-1.5 border border-[#007AFF]/50 text-[#3B9BFF] hover:bg-[#007AFF]/10 px-3 py-1.5 text-[10px] font-mono font-bold uppercase tracking-widest"
                  data-testid="chart-retry-btn">
            <RefreshCw className="h-3 w-3" /> Retry now
          </button>
        </div>
      )}

      {/* Apache-2.0 attribution requirement */}
      <div className="px-2 py-1 text-[9px] font-mono text-zinc-600 flex justify-end">
        <a href="https://www.tradingview.com/" target="_blank" rel="noreferrer" className="hover:text-zinc-400" data-testid="lw-attribution">
          Charting by TradingView Lightweight Charts™
        </a>
      </div>
    </div>
  );
}
